/**
 * Static copy for landing / market-signals regime cards.
 * Aligned with docs/Manual/ai-trading-bot/AI-Trading-Bot-API.html (#regime-types-reference, #regime-vs-btc-badge).
 */

export type MarketSignalExplainerKind = 'ai_regime' | 'btc_regime' | 'participation';

export interface ExplainerStatus {
  /** Internal code (AI regimes only) — shown in advanced section */
  code?: string;
  label: string;
  meaning: string;
  trigger?: string;
}

export interface MarketSignalExplainerContent {
  kind: MarketSignalExplainerKind;
  title: string;
  intro: string;
  statuses: ExplainerStatus[];
  relatedCards: string;
  advancedLines: string[];
  maskedMessage: string;
}

export const MARKET_SIGNAL_EXPLAINERS: Record<MarketSignalExplainerKind, MarketSignalExplainerContent> = {
  ai_regime: {
    kind: 'ai_regime',
    title: 'AI Market Regime',
    intro:
      'A combined read of meme participation, Bitcoin direction, and average meme volatility. ' +
      'The same classification powers Create AI Strategy and AI Auto-Tune — it is not the same label as the BTC card alone.',
    maskedMessage:
      'Live AI regime values are reserved for subscribers with market-signals access. Below is what each status means when data is visible.',
    statuses: [
      {
        code: 'LOW_PARTICIPATION',
        label: 'Low Participation',
        trigger: 'Participation level LOW, or fewer than 25% of tracked meme coins active',
        meaning: 'Quiet meme market — fewer coins moving with real volume. Defensive trading posture; reduced new-trade expectancy.',
      },
      {
        code: 'BEARISH_BTC',
        label: 'Bearish BTC Environment',
        trigger: 'BTC 24h change ≤ −3% (and participation is not already low)',
        meaning: 'Macro risk-off backdrop. Long meme entries are higher risk; defensive filters and tighter BTC gates apply.',
      },
      {
        code: 'HIGH_VOLATILITY',
        label: 'High Volatility',
        trigger: 'Average volatility of top meme coins > 12%',
        meaning: 'Wide price swings — more opportunity but elevated risk. Wider stops and stricter entry filters are typical.',
      },
      {
        code: 'HOT_MEME',
        label: 'Hot Meme Rally',
        trigger: 'Participation ≥ 55%, volatility between 4% and 40%, BTC not bearish',
        meaning: 'Broad meme momentum — many coins participating. Favourable conditions for momentum-style setups.',
      },
      {
        code: 'MODERATE',
        label: 'Balanced / Moderate',
        trigger: 'None of the above — mixed “normal” conditions',
        meaning: 'No extreme signal matched. Default balanced parameters; gates may still adapt to live readings.',
      },
    ],
    relatedCards:
      'BTC can show RANGING while this card shows Low Participation, High Volatility, or Moderate — read the BTC and Meme Participation cards together. ' +
      'Example: BTC RANGING + LOW participation often maps to Low Participation here; RANGING + high vol maps to High Volatility.',
    advancedLines: [
      'Classification priority (first match wins): Low Participation → Bearish BTC → High Volatility → Hot Meme Rally → Moderate.',
      'Shares live data with PublicMarketSignalService and AiStrategyConfigSuggestionService.',
      'Updates on the same refresh cycle as the other signal cards (~60 seconds).',
    ],
  },

  btc_regime: {
    kind: 'btc_regime',
    title: 'BTC Market Regime',
    intro:
      'Measures only Bitcoin’s 24-hour price change. It does not look at meme coin participation or volatility. ' +
      'Some trading bots use this as a macro filter; the AI Market Regime card adds meme-market context on top.',
    maskedMessage:
      'Live BTC regime values are reserved for subscribers with market-signals access. Below is what each status means when data is visible.',
    statuses: [
      {
        label: 'BULLISH',
        trigger: 'BTC 24h change ≥ +3%',
        meaning: 'Bitcoin rallying — altcoins and meme coins often follow upward momentum.',
      },
      {
        label: 'BEARISH',
        trigger: 'BTC 24h change ≤ −3%',
        meaning: 'Bitcoin selling off — elevated risk environment for long exposure.',
      },
      {
        label: 'RANGING',
        trigger: 'BTC 24h change between −3% and +3%',
        meaning: 'Bitcoin consolidating — no strong directional bias from BTC alone.',
      },
      {
        label: 'UNKNOWN',
        trigger: 'Price data temporarily unavailable',
        meaning: 'KuCoin feed error or stale data — retry shortly; do not treat as a trading signal.',
      },
    ],
    relatedCards:
      'RANGING here does not mean “moderate meme market.” Check Meme Participation for coin activity and AI Market Regime for the combined bot-oriented label.',
    advancedLines: [
      'Source: PublicMarketSignalService.getBtcRegime() — same ±3% rule used by AI Bearish BTC classification.',
      'There is no AI regime called BULLISH_BTC or RANGING; those are inputs to the five AI presets only.',
    ],
  },

  participation: {
    kind: 'participation',
    title: 'Meme Participation',
    intro:
      'Shows how many tracked meme futures coins are “actively moving” — positive 24h price change plus volume above the pool average. ' +
      'This is raw activity, not a trade recommendation.',
    maskedMessage:
      'Live participation values are reserved for subscribers with market-signals access. Below is what each level means when data is visible.',
    statuses: [
      {
        label: 'HIGH ACTIVITY',
        trigger: '≥ 60% of tracked coins active',
        meaning: 'Broad momentum — many meme names participating; stronger meme-market backdrop.',
      },
      {
        label: 'MODERATE',
        trigger: '35%–59% of tracked coins active',
        meaning: 'Mixed market — some coins moving, others flat. Typical mid-range conditions.',
      },
      {
        label: 'LOW ACTIVITY',
        trigger: '< 35% of tracked coins active',
        meaning: 'Thin participation — few coins with meaningful movement; often aligns with AI Low Participation.',
      },
      {
        label: 'LOADING…',
        trigger: 'Data unavailable or unreliable',
        meaning: 'Feed temporarily down or rate-limited — wait for the next refresh.',
      },
    ],
    relatedCards:
      'Low activity here often drives AI Market Regime → Low Participation (checked before bearish BTC). ' +
      'BTC RANGING + MODERATE participation can still mean different AI regimes depending on volatility.',
    advancedLines: [
      'Active coin = positive 24h move AND volume above ~50% of the participation pool average.',
      'Bar shows activeCoins / totalCoins from the meme futures universe (typically ~12 pairs on the public page).',
      'Same participation snapshot used by AI classification when participation rate < 25%.',
    ],
  },
};
